import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n/LocaleProvider";

// ---------------------------------------------------------------------------
// SWEEP S15. A PICKER THAT WORKS AT ANY SIZE.
//
// The client picker on New event, the bench pickers in Roles and Privacy loaded one page
// — 50 clients, 50 or 100 people — and offered nothing beyond it: the 51st client could
// not have an event, the 101st worker could not be checked or have a privacy request.
// This asks the server with what is typed (`q`), so the list is the matches, not the
// first page. A plain <select> stays underneath, so keyboard and screen-reader use is the
// browser's own.
// ---------------------------------------------------------------------------

export type Option = { id: string; label: string };

export function SearchSelect({
  id,
  label,
  value,
  onChange,
  search,
  placeholder,
  emptyLabel,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (id: string) => void;
  search: (q: string) => Promise<Option[]>;
  placeholder?: string;
  emptyLabel?: string;
}) {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [chosen, setChosen] = useState<Option | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    const t = window.setTimeout(() => {
      search(q.trim())
        .then((found) => {
          // ONLY THE LATEST ANSWER LANDS, so a slow reply to "Har" cannot overwrite "Harbour".
          if (mine === seq.current) setOptions(found);
        })
        .catch(() => {
          if (mine === seq.current) setOptions([]);
        });
    }, 250);
    return () => window.clearTimeout(t);
  }, [q, search]);

  // The chosen option stays listed even when the search moves on, so the select never
  // shows a value it does not hold.
  const list = chosen && !options.some((o) => o.id === chosen.id) ? [chosen, ...options] : options;

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        type="search"
        aria-label={`Search: ${label}`}
        value={q}
        placeholder={placeholder ?? t("Type to search")}
        onChange={(e) => setQ(e.target.value)}
      />
      <select
        id={id}
        value={value}
        onChange={(e) => {
          setChosen(list.find((o) => o.id === e.target.value) ?? null);
          onChange(e.target.value);
        }}
      >
        <option value="">{list.length === 0 && q.trim() !== "" ? t("Nothing matches") : (emptyLabel ?? t("Choose…"))}</option>
        {list.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
